import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Client } from '../../http';
import { RootState } from '../../app/store';

export type DiscrepancyStatus =
  | 'mismatch'
  | 'missing_in_hubspot'
  | 'contact_not_found';

export interface Discrepancy {
  email: string;
  cb_customer_id: string;
  dynamo_crn: string;
  hubspot_crn: string;
  status: DiscrepancyStatus;
}

export interface ReconciliationReport {
  scanned: number;
  compared: number;
  matched: number;
  mismatched: number;
  missing_in_hubspot: number;
  contact_not_found: number;
  skipped_no_email: number;
  truncated: boolean;
  discrepancies: Discrepancy[];
}

// ResolveResult is the per-item outcome returned by the write-back endpoints.
export interface ResolveResult {
  email: string;
  dynamo_crn: string;
  hubspot_crn: string;
  applied: boolean;
  crn?: string;
  reason?: string;
}

export interface ResolveReport {
  applied: number;
  skipped: number;
  failed: number;
  results: ResolveResult[];
}

export type ResolveAction = 'populate-hubspot' | 'apply-hubspot' | 'force-hubspot';

export interface ReconciliationState {
  report: ReconciliationReport | null;
  status: 'idle' | 'loading' | 'failed';
  error: string | null;
  // resolving tracks an in-flight write-back action (null when idle).
  resolving: ResolveAction | null;
  // resolveReport holds the outcome of the last write-back action.
  resolveReport: ResolveReport | null;
  resolveError: string | null;
}

const initialState: ReconciliationState = {
  report: null,
  status: 'idle',
  error: null,
  resolving: null,
  resolveReport: null,
  resolveError: null,
};

export interface RunReconciliationArgs {
  limit?: number;
  includeFreed?: boolean;
}

/**
 * Run the CRN reconciliation: scans DynamoDB accounts and compares each CRN to
 * the matching HubSpot contact's customer_reference_number.
 */
export const runCRNReconciliation = createAsyncThunk<
  ReconciliationReport,
  RunReconciliationArgs,
  { rejectValue: string }
>(
  'reconciliation/runCRN',
  async (arg, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (arg.limit) params.set('limit', String(arg.limit));
      if (arg.includeFreed) params.set('include_freed', 'true');
      const qs = params.toString();
      const url = `/api/v1/admin/reconcile/crn${qs ? `?${qs}` : ''}`;
      const response = await Client.get<ReconciliationReport>(url);
      return response.data;
    } catch (e: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      const msg = e?.response?.data?.error ?? 'Failed to run reconciliation';
      return rejectWithValue(msg);
    }
  },
);

// ResolveItem mirrors a discrepancy row sent to the write-back endpoints.
export interface ResolveItem {
  email: string;
  cb_customer_id: string;
  dynamo_crn: string;
  hubspot_crn: string;
}

export interface RunResolveArgs {
  action: ResolveAction;
  items: ResolveItem[];
}

/**
 * Run a CRN write-back action against the reconciler:
 *  - 'populate-hubspot': write the Dynamo CRN into HubSpot for missing contacts.
 *  - 'apply-hubspot': push the HubSpot CRN into Chargebee + DynamoDB for
 *    mismatched records whose HubSpot CRN is not already in use.
 *  - 'force-hubspot': overwrite HubSpot with the Dynamo CRN (source of truth),
 *    including mismatches. Chargebee/DynamoDB are left untouched.
 */
export const runReconciliationResolve = createAsyncThunk<
  ResolveReport,
  RunResolveArgs,
  { rejectValue: string }
>(
  'reconciliation/resolve',
  async (arg, { rejectWithValue }) => {
    try {
      const url = `/api/v1/admin/reconcile/crn/${arg.action}`;
      const response = await Client.post<ResolveReport>(url, { items: arg.items });
      return response.data;
    } catch (e: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      const msg = e?.response?.data?.error ?? 'Failed to apply reconciliation changes';
      return rejectWithValue(msg);
    }
  },
);

export const reconciliationSlice = createSlice({
  name: 'reconciliation',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(runCRNReconciliation.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        // A fresh scan invalidates any previous write-back outcome.
        state.resolveReport = null;
        state.resolveError = null;
      })
      .addCase(runCRNReconciliation.fulfilled, (state, action) => {
        state.status = 'idle';
        state.report = action.payload;
      })
      .addCase(runCRNReconciliation.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'Failed to run reconciliation';
      })
      .addCase(runReconciliationResolve.pending, (state, action) => {
        state.resolving = action.meta.arg.action;
        state.resolveError = null;
      })
      .addCase(runReconciliationResolve.fulfilled, (state, action) => {
        state.resolving = null;
        state.resolveReport = action.payload;
      })
      .addCase(runReconciliationResolve.rejected, (state, action) => {
        state.resolving = null;
        state.resolveError = action.payload ?? 'Failed to apply reconciliation changes';
      });
  },
});

export const selectReconciliationReport = (state: RootState) => state.reconciliation.report;
export const selectReconciliationStatus = (state: RootState) => state.reconciliation.status;
export const selectReconciliationError = (state: RootState) => state.reconciliation.error;
export const selectReconciliationResolving = (state: RootState) => state.reconciliation.resolving;
export const selectReconciliationResolveReport = (
  state: RootState,
) => state.reconciliation.resolveReport;
export const selectReconciliationResolveError = (
  state: RootState,
) => state.reconciliation.resolveError;

export default reconciliationSlice.reducer;
